/**
 * Cross-catalog fallback search for the biomarker/disease explorer surfaces.
 *
 * When a scoped search returns 0 results (an active package filter hides the
 * term, or a test page only carries a subset), this re-runs the SAME tiered
 * matcher (`createPageSearcher`) against the full, unfiltered catalog and
 * resolves each hit to the test(s) that include it — so we can say
 * "Looking for Testosterone? It's part of the Advanced blood test." The
 * caller resolves each test's localized label from `primaryTest.translationKey`.
 *
 * Pure + deterministic: no React, no I/O. The caller owns data fetching and
 * decides when results are zero.
 */
import { PACKAGE_FILTER_ORDER, PACKAGE_TO_TEST_SLUG } from "../categories"
import { isAvailable } from "../availability"
import { getTestBySlug } from "../../data/tests"
import { createPageSearcher, type SearchableItem } from "./page-filter"

/** A catalog row the matcher can both search and resolve to a test. Both
 *  `CMSBiomarkerListItem` and `CMSDisease` satisfy this structurally. */
export interface CrossCatalogItem extends SearchableItem {
  slug: string
  packages: string[]
}

export interface ResolvedTest {
  slug: string
  packageId: string
  /** `whatWeDetect` translation key for the package's localized label. The
   *  caller resolves it with its `wt` translator — PACKAGE_LABELS is
   *  English-only and would leak English into de/fr/nl pages. */
  translationKey: string
}

export interface CrossCatalogMatch {
  item: CrossCatalogItem
  /** Distinct linkable tests that include this item, deduped by test slug and
   *  ordered specific-first (the caller shows the first few, then "+N more"). */
  tests: ResolvedTest[]
}

/** package id → its index in the canonical filter order (for stable picking). */
const PACKAGE_ORDER_INDEX: Record<string, number> = Object.fromEntries(
  PACKAGE_FILTER_ORDER.map((p, i) => [p.id, i])
)

/** package id → its `whatWeDetect` filter-label translation key. */
const PACKAGE_TRANSLATION_KEY: Record<string, string> = Object.fromEntries(
  PACKAGE_FILTER_ORDER.map((p) => [p.id, p.translationKey])
)

/** The all-inclusive MRI bundles (core/advanced/pro all map to the full-body-MRI
 *  test). Nearly every biomarker lives in `pro`, so if we ranked by raw filter
 *  order these bundles would win every suggestion ("It's part of Pro MRI") and
 *  bury the specific, more useful test. Deprioritize them so a targeted test —
 *  the hormone panel for testosterone, the blood test for ferritin — wins, and
 *  the bundle only surfaces when it's the sole option. */
const MRI_BUNDLE_PACKAGES = new Set(["core", "advanced", "pro"])

/** Resolve an item's packages to distinct, linkable tests, ordered by the
 *  canonical package order, excluding any in-scope packages. */
function resolveTests(
  item: CrossCatalogItem,
  exclude: Set<string>,
  isLinkable: (slug: string) => boolean
): ResolvedTest[] {
  const ordered = item.packages
    .filter(
      (p) =>
        PACKAGE_TO_TEST_SLUG[p] &&
        !exclude.has(p) &&
        isLinkable(PACKAGE_TO_TEST_SLUG[p])
    )
    .sort((a, b) => {
      // Specific tests before the catch-all MRI bundles.
      const aBundle = MRI_BUNDLE_PACKAGES.has(a) ? 1 : 0
      const bBundle = MRI_BUNDLE_PACKAGES.has(b) ? 1 : 0
      if (aBundle !== bBundle) return aBundle - bBundle
      return (
        (PACKAGE_ORDER_INDEX[a] ?? Number.MAX_SAFE_INTEGER) -
        (PACKAGE_ORDER_INDEX[b] ?? Number.MAX_SAFE_INTEGER)
      )
    })

  const seenSlug = new Set<string>()
  const tests: ResolvedTest[] = []
  for (const packageId of ordered) {
    const slug = PACKAGE_TO_TEST_SLUG[packageId]
    if (seenSlug.has(slug)) continue
    seenSlug.add(slug)
    tests.push({
      slug,
      packageId,
      translationKey: PACKAGE_TRANSLATION_KEY[packageId],
    })
  }
  return tests
}

export function findCrossCatalogMatches(args: {
  query: string
  allItems: readonly CrossCatalogItem[]
  locale: string
  /** Packages already in scope (active filter / the test's own packages). */
  excludePackages?: string[]
  /** Max suggestable matches to return. Default 3. */
  limit?: number
  /** Don't suggest below this query length — a 2-char prefix like "te" hits
   *  "testosterone" via tier-1 but isn't a deliberate search yet. Default 3,
   *  matching the page search-tracking threshold. */
  minQueryLength?: number
  /** Per-item cross-locale aliases (other-locale names), so the fallback has
   *  the same cross-language reach as the in-scope page search. */
  augmentAliases?: (item: CrossCatalogItem) => readonly string[]
  /** Market whose test pages may be linked. A test whose `availability`
   *  denies this country × locale (e.g. the CH-only blood add-ons on /nl/nl)
   *  404s there, so it is never suggested. Omit to allow every test. */
  country?: string
}): CrossCatalogMatch[] {
  const {
    query,
    allItems,
    locale,
    excludePackages = [],
    limit = 3,
    minQueryLength = 3,
    augmentAliases,
    country,
  } = args
  if (query.trim().length < minQueryLength) return []

  const exclude = new Set(excludePackages)
  const isLinkable = (slug: string) =>
    !country || isAvailable(getTestBySlug(slug)?.availability, country, locale)
  const searcher = createPageSearcher(allItems, locale, { augmentAliases })
  const hits = searcher.filter(query)

  const matches: CrossCatalogMatch[] = []
  for (const item of hits) {
    const tests = resolveTests(item, exclude, isLinkable)
    if (tests.length === 0) continue // not suggestable (no linkable / all in scope)
    matches.push({ item, tests })
    if (matches.length >= limit) break
  }
  return matches
}
